import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaBriefcase, FaMapMarkerAlt, FaCheckCircle } from 'react-icons/fa'
import { useProfileStatus } from '@/hooks/useProfile'
import { useServiceOfferings, useSelectServices } from '@/hooks/useServices'
import ServicesStep from '@/components/steps/ServicesStep'
import LocationStep from '@/components/steps/LocationStep'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Spinner from '@/components/common/Spinner/Spinner'
import routes from '@/routes/routes'
import './ProviderOnboarding.css'

const ProviderOnboarding = () => {
    const navigate = useNavigate()
    const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useProfileStatus()
    const { data: offeringsData, isLoading: offeringsLoading } = useServiceOfferings()
    const selectServices = useSelectServices()
    const queryClient = useQueryClient()
    
    const [currentStep, setCurrentStep] = useState<'services' | 'districts'>('services')
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([])
    const [savingServices, setSavingServices] = useState(false)

    // Initialize selected services from existing offerings
    useEffect(() => {
        if (Array.isArray(offeringsData) && offeringsData.length > 0) {
            const ids = offeringsData
                .map((offering: any) => {
                    if (typeof offering.service === 'number') return offering.service
                    if (offering.service?.id) return offering.service.id
                    return undefined
                })
                .filter((id: number | undefined): id is number => typeof id === 'number')
            if (ids.length > 0) {
                setSelectedServiceIds(ids)
            }
        }
    }, [offeringsData])

    const isLoading = statusLoading || offeringsLoading

    // Check if onboarding is complete
    const hasServices = status?.has_services ?? false
    const hasDistricts = status?.has_districts ?? false

    // Auto-advance to districts if services are already selected
    useEffect(() => {
        if (!isLoading && hasServices && !hasDistricts && currentStep === 'services') {
            setCurrentStep('districts')
        }
    }, [hasServices, hasDistricts, isLoading, currentStep])

    // Navigate to provider requests when onboarding is complete
    useEffect(() => {
        if (hasServices && hasDistricts && !isLoading) {
            // Small delay to ensure status is updated
            const timer = setTimeout(() => {
                navigate(routes.providerRequests)
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [hasServices, hasDistricts, isLoading, navigate])

    const handleServicesComplete = async () => {
        if (selectedServiceIds.length === 0) {
            toast.error('Selecciona al menos un servicio para continuar.')
            return
        }

        setSavingServices(true)
        try {
            await selectServices.mutateAsync({
                services: selectedServiceIds.map(serviceId => ({ service_id: serviceId })),
            })
            // Invalidate queries to refresh status
            await queryClient.invalidateQueries({ queryKey: ['profile-status'] })
            await queryClient.invalidateQueries({ queryKey: ['service-offerings'] })
            await refetchStatus()
            toast.success('Servicios guardados correctamente')
            setCurrentStep('districts')
        } catch (error: any) {
            console.error('Error saving services:', error)
            toast.error(error?.response?.data?.detail || 'No se pudieron guardar los servicios. Intenta nuevamente.')
        } finally {
            setSavingServices(false)
        }
    }

    const handleDistrictsComplete = async () => {
        // Invalidate queries to refresh status
        await queryClient.invalidateQueries({ queryKey: ['profile-status'] })
        await queryClient.invalidateQueries({ queryKey: ['provider-profile'] })
        await refetchStatus()
        // The parent component will detect completion and show the home page
    }

    if (isLoading) {
        return (
            <div className="provider-onboarding-page">
                <div className="provider-onboarding-container">
                    <div className="provider-onboarding-loading">
                        <Spinner />
                        <p>Cargando configuración...</p>
                    </div>
                </div>
            </div>
        )
    }

    // If both steps are complete, don't show onboarding (parent should handle this)
    if (hasServices && hasDistricts) {
        return null
    }

    return (
        <div className="provider-onboarding-page">
            <div className="provider-onboarding-container">
                <div className="provider-onboarding-card">
                    {/* Progress indicator */}
                    <div className="provider-onboarding-progress">
                        <div className={`provider-onboarding-step ${currentStep === 'services' ? 'active' : 'completed'}`}>
                            <div className="provider-onboarding-step-icon">
                                {currentStep === 'services' ? <FaBriefcase /> : <FaCheckCircle />}
                            </div>
                            <div className="provider-onboarding-step-content">
                                <span className="provider-onboarding-step-number">Paso 1</span>
                                <span className="provider-onboarding-step-label">Selecciona servicios</span>
                            </div>
                        </div>
                        <div className={`provider-onboarding-progress-line ${currentStep === 'districts' || hasDistricts ? 'completed' : ''}`}></div>
                        <div className={`provider-onboarding-step ${currentStep === 'districts' ? 'active' : hasDistricts ? 'completed' : ''}`}>
                            <div className="provider-onboarding-step-icon">
                                {hasDistricts ? <FaCheckCircle /> : <FaMapMarkerAlt />}
                            </div>
                            <div className="provider-onboarding-step-content">
                                <span className="provider-onboarding-step-number">Paso 2</span>
                                <span className="provider-onboarding-step-label">Selecciona distritos</span>
                            </div>
                        </div>
                    </div>

                    {/* Step content */}
                    <div className="provider-onboarding-content">
                        {currentStep === 'services' ? (
                            <div>
                                <ServicesStep
                                    initialSelected={selectedServiceIds}
                                    onSelectionChange={setSelectedServiceIds}
                                    onComplete={handleServicesComplete}
                                />
                                <div className="provider-onboarding-actions">
                                    <button
                                        className="provider-onboarding-button"
                                        onClick={handleServicesComplete}
                                        disabled={selectedServiceIds.length === 0 || savingServices}
                                    >
                                        {savingServices ? 'Guardando...' : 'Continuar'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <LocationStep onComplete={handleDistrictsComplete} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProviderOnboarding

